import type { EngineTelemetrySnapshot } from '@sorrem/engine';
import type { UIGameState } from '@sorrem-client/types';
import type { ClientMetricEventRecord } from './client-metric-reporter';

/** How much play one `client_perf_sampled` event covers. */
export const PERF_SAMPLE_WINDOW_MS = 60_000;

/** What the page looks like to the player when a sample is taken. */
export interface ClientDisplayInfo {
    readonly viewportWidth: number;
    readonly viewportHeight: number;
    readonly devicePixelRatio: number;
    readonly gpuRenderer?: string;
}

export interface ClientRunMetricsOptions {
    readonly crucibleId: string;
    readonly record: (event: ClientMetricEventRecord) => void;
    readonly getDisplayInfo: () => ClientDisplayInfo;
    readonly now?: () => Date;
}

/**
 * Watches one run's UI state for the client metric events it implies (#213):
 * `trial_result_inspected` once the run's result is shown, and a
 * `client_perf_sampled` for each minute of frame timing.
 *
 * The engine publishes a snapshot about every 250 ms of a one-second window
 * that resets, so a snapshot's averages cover at least a quarter second. The
 * frame-time percentiles are over those snapshots; average FPS comes from the
 * running frame total; catch-up frames are counted as each window closes.
 */
export class ClientRunMetrics {
    private readonly options: ClientRunMetricsOptions;
    private readonly now: () => Date;
    private hasReportedResult = false;
    private sampleStart: EngineTelemetrySnapshot | null = null;
    private frameTimes: number[] = [];
    private renderTimes: number[] = [];
    private frameTimeMaxMs = 0;
    private catchUpFrames = 0;
    private windowCatchUpFrames = 0;

    constructor(options: ClientRunMetricsOptions) {
        this.options = options;
        this.now = options.now ?? ((): Date => new Date());
    }

    /** Takes one UI state patch from the game. */
    observe(patch: Partial<UIGameState>): void {
        if (patch.runResult && !this.hasReportedResult) {
            this.hasReportedResult = true;
            this.options.record({
                eventName: 'trial_result_inspected',
                occurredAt: this.now().toISOString(),
                crucibleId: this.options.crucibleId,
                payload: {
                    outcome: patch.runResult.outcome,
                    reason: patch.runResult.reason,
                    elapsedMs: patch.runResult.elapsedMs,
                },
            });
        }

        if (patch.telemetry) {
            this.addSnapshot(patch.telemetry);
        }
    }

    private addSnapshot(snapshot: EngineTelemetrySnapshot): void {
        // Nothing drawn yet (loading), or the run is over: neither is play.
        if (snapshot.totalFrameCount === 0 || this.hasReportedResult) {
            return;
        }

        if (this.sampleStart === null) {
            this.sampleStart = snapshot;
            this.windowCatchUpFrames = snapshot.catchUpFrameCount;
            return;
        }

        // A lower count than last time means the engine started a new window.
        if (snapshot.catchUpFrameCount < this.windowCatchUpFrames) {
            this.catchUpFrames += this.windowCatchUpFrames;
        }
        this.windowCatchUpFrames = snapshot.catchUpFrameCount;
        this.frameTimes.push(snapshot.averageFrameTimeMs);
        this.renderTimes.push(snapshot.averageRenderTimeMs);
        this.frameTimeMaxMs = Math.max(this.frameTimeMaxMs, snapshot.maxFrameTimeMs);

        const elapsedMs = snapshot.timestampMs - this.sampleStart.timestampMs;
        if (elapsedMs >= PERF_SAMPLE_WINDOW_MS) {
            this.emitSample(snapshot, elapsedMs);
        }
    }

    private emitSample(end: EngineTelemetrySnapshot, elapsedMs: number): void {
        const start = this.sampleStart;
        if (start === null) {
            return;
        }

        const sorted = [...this.frameTimes].sort((a, b) => a - b);
        this.options.record({
            eventName: 'client_perf_sampled',
            occurredAt: this.now().toISOString(),
            crucibleId: this.options.crucibleId,
            payload: {
                sampleWindowMs: elapsedMs,
                fpsAverage: round(((end.totalFrameCount - start.totalFrameCount) * 1000) / elapsedMs),
                frameTimeP50Ms: round(percentile(sorted, 0.5)),
                frameTimeP95Ms: round(percentile(sorted, 0.95)),
                frameTimeMaxMs: round(this.frameTimeMaxMs),
                renderTimeAverageMs: round(average(this.renderTimes)),
                catchUpFrameCount: this.catchUpFrames + this.windowCatchUpFrames,
                ...this.options.getDisplayInfo(),
            },
        });

        // The next sample starts here; this window's catch-up frames are counted already.
        this.sampleStart = end;
        this.frameTimes = [];
        this.renderTimes = [];
        this.frameTimeMaxMs = 0;
        this.catchUpFrames = -this.windowCatchUpFrames;
    }
}

/** The value at fraction `p` of an ascending list (nearest rank). */
function percentile(sorted: readonly number[], p: number): number {
    if (sorted.length === 0) {
        return 0;
    }
    const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
    return sorted[index] ?? 0;
}

function average(values: readonly number[]): number {
    return values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;
}

function round(value: number): number {
    return Math.round(value * 100) / 100;
}
